import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ClipboardCheck } from "lucide-react";
import { adminActivityReviews, adminReviewItem } from "@/lib/api";

const GROUPS = [
  { kind: "services", label: "Professional service listings", title: (x) => `${x.name} · ${x.provider || ""}`, sub: (x) => `PIN ${x.pincode || "—"} · ${x.unit || ""}` },
  { kind: "jobs", label: "Job posts", title: (x) => `${x.title} · ${x.company || ""}`, sub: (x) => `PIN ${x.pincode} · ${x.type}` },
  { kind: "categories", label: "New category suggestions", title: (x) => x.name, sub: () => "Suggested by a user" },
];

export default function AdminActivityReviews() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => adminActivityReviews().then(setData).catch(() => setData({ services: [], jobs: [], categories: [] }));
  useEffect(() => { load(); }, []);
  const act = async (kind, id, action) => {
    setBusy(id);
    try { await adminReviewItem(kind, id, action); toast.success(action === "approve" ? "Approved" : "Rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };
  if (!data) return <div className="text-sm text-slate-500 py-8 text-center">Loading...</div>;
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-5 sm:p-6 space-y-5" data-testid="activity-reviews-section">
      <h2 className="font-display text-xl font-bold flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-emerald-700" /> Listing Reviews</h2>
      {GROUPS.map((g) => (
        <div key={g.kind}>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{g.label} ({data[g.kind].length})</div>
          {data[g.kind].length === 0 ? <div className="text-xs text-slate-400" data-testid={`review-empty-${g.kind}`}>Nothing pending.</div> : (
            <div className="space-y-2">
              {data[g.kind].map((x) => (
                <div key={x.id} className="border border-slate-100 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`review-${g.kind}-${x.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{g.title(x)}</div>
                    <div className="text-[11px] text-slate-500 truncate">{g.sub(x)}</div>
                    {x.description && <div className="text-xs text-slate-600 line-clamp-2 mt-0.5">{x.description}</div>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => act(g.kind, x.id, "approve")} disabled={busy === x.id} className="inline-flex items-center gap-1 rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white px-3 py-1.5 text-xs font-semibold" data-testid={`review-approve-${x.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
                    <button onClick={() => act(g.kind, x.id, "reject")} disabled={busy === x.id} className="inline-flex items-center gap-1 rounded-full bg-rose-100 hover:bg-rose-200 text-rose-700 px-3 py-1.5 text-xs font-semibold" data-testid={`review-reject-${x.id}`}><XCircle className="w-3.5 h-3.5" /> Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <div data-testid="admin-expired-jobs">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Expired job posts — history, read-only ({(data.expired_jobs || []).length})</div>
        {(data.expired_jobs || []).length === 0 ? <div className="text-xs text-slate-400">No expired job posts.</div> : (
          <div className="space-y-1.5">
            {data.expired_jobs.map((x) => (
              <div key={x.id} className="border border-slate-100 rounded-xl px-3 py-2 text-sm flex flex-wrap items-center gap-x-2 min-w-0" data-testid={`expired-job-${x.id}`}>
                <span className="font-semibold truncate min-w-0">{x.title}</span><span className="text-slate-500 truncate">· {x.company || "—"} · PIN {x.pincode}</span>
                <span className="ms-auto text-[11px] font-bold uppercase bg-rose-50 border border-rose-200 text-rose-800 px-1.5 py-0.5 rounded">Expired {x.last_date || ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
